// Inline editor for ParagraphBlock + HeadingBlock content.
//
// A contenteditable surface where users type prose freely. Typing "@" opens
// a variable picker; selecting inserts an unbreakable chip. Clicking a chip
// opens a filter editor so authors can write sentences like:
//   "Hi {{ subject.subject_name }}, based in {{ subject.jurisdictions | format_jurisdictions }}."
//
// Chips are `contentEditable={false}` spans with `data-var-path` and
// (optionally) `data-var-filters` so we can walk the DOM back into the
// InlineNode[] model on every input event.
//
// React doesn't reliably control contenteditable, so we treat the DOM as
// uncontrolled after first mount — re-rendering only when `value` changes
// from outside (parent state). Local typing flows through `onInput` →
// `domToNodes` → `onChange`.

import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ActionIcon,
  Button,
  Code,
  Combobox,
  Group,
  Paper,
  Popover,
  Portal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core';
import { IconPlus, IconTrash, IconX } from '@tabler/icons-react';

import { FilterApplication, InlineNode } from '@/types';
import {
  appendNodes,
  chipLabelWithFilters,
  domToNodes,
  ensureChipCaretSafety,
  filterToJinja,
  labelForPath,
  makeChipElement,
  VAR_FILTERS_ATTR,
  VAR_PATH_ATTR,
  ZWSP,
} from './inline-editor-dom';
import classes from './TemplateEditor.module.css';

export interface VariableOption {
  // Dotted path, e.g. "subject.subject_name" or "executive_summary".
  path: string;
  // Display label (e.g. "Subject name").
  label: string;
  // Group header, e.g. "Subject fields", "Global fields".
  group: string;
  // Short description for tooltip / secondary label.
  description?: string;
}

export interface FilterOption {
  name: string;
  description: string;
}

interface InlineEditorProps {
  value: InlineNode[];
  onChange: (next: InlineNode[]) => void;
  variables: VariableOption[];
  // Filters the user can attach to inline variable chips. Optional — when
  // omitted, chips become non-clickable (read-only).
  filterOptions?: FilterOption[];
  placeholder?: string;
  // Visual style — heading needs larger font, paragraph normal.
  flavour?: 'paragraph' | 'heading';
  // For a11y / form labelling.
  ariaLabel?: string;
}

interface ChipEditorState {
  element: HTMLElement;
  rect: DOMRect;
  path: string;
  filters: FilterApplication[];
}

export function InlineEditor({
  value,
  onChange,
  variables,
  filterOptions = [],
  placeholder,
  flavour = 'paragraph',
  ariaLabel,
}: Readonly<InlineEditorProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<InlineNode[]>(value);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  // Stored range where the user typed "@", so we can replace it on selection.
  const triggerRangeRef = useRef<Range | null>(null);

  // Chip-click filter editor.
  const [chipEditor, setChipEditor] = useState<ChipEditorState | null>(null);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Sync `value` prop → DOM when it changes externally. Avoid touching the DOM
  // during local typing — that would clobber the cursor.
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    if (value === lastValueRef.current) {
      return;
    }
    if (JSON.stringify(value) === JSON.stringify(lastValueRef.current)) {
      return;
    }
    ref.current.innerHTML = '';
    appendNodes(ref.current, value, { chipClassName: classes.varChip });
    lastValueRef.current = value;
  }, [value]);

  // Initial mount: render the value into the DOM once.
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    if (ref.current.childNodes.length > 0) {
      return;
    }
    appendNodes(ref.current, value, { chipClassName: classes.varChip });
    lastValueRef.current = value;
    // Intentionally only run on mount. Subsequent value changes are handled
    // by the separate sync-from-prop effect above; including `value` here
    // would cause double-renders and clobber the user's cursor.
  }, []);

  const fireChange = useCallback(() => {
    if (!ref.current) {
      return;
    }
    const nodes = domToNodes(ref.current);
    lastValueRef.current = nodes;
    onChange(nodes);
  }, [onChange]);

  const handleInput = useCallback(() => {
    fireChange();
  }, [fireChange]);

  const openPickerAtCursor = useCallback(() => {
    const sel = globalThis.getSelection?.();
    if (!sel || sel.rangeCount === 0) {
      return;
    }
    triggerRangeRef.current = sel.getRangeAt(0).cloneRange();
    setPickerQuery('');
    setPickerOpen(true);
    combobox.openDropdown();
  }, [combobox]);

  const tryDeleteAdjacentChip = useCallback(
    (key: 'Backspace' | 'Delete'): boolean => {
      const sel = globalThis.getSelection?.();
      if (!sel || sel.rangeCount !== 1 || !sel.isCollapsed) {
        return false;
      }
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) {
        return false;
      }
      const text = node.textContent ?? '';
      const offset = range.startOffset;

      if (key === 'Backspace') {
        // Backspace removes the previous chip if the cursor sits at offset 0
        // OR at offset 1 of a text node that begins with a ZWSP (the caret-
        // safety pad). Either way, the previous sibling is the chip to delete.
        const prev = node.previousSibling;
        const atChipBoundary =
          (offset === 0 || (offset === 1 && text.startsWith(ZWSP))) &&
          prev instanceof HTMLElement &&
          prev.hasAttribute(VAR_PATH_ATTR);
        if (atChipBoundary) {
          prev.remove();
          fireChange();
          return true;
        }
        return false;
      }

      // Delete: symmetric, forward-delete the next chip.
      const next = node.nextSibling;
      const atChipBoundary =
        (offset === text.length || (offset === text.length - 1 && text.endsWith(ZWSP))) &&
        next instanceof HTMLElement &&
        next.hasAttribute(VAR_PATH_ATTR);
      if (atChipBoundary) {
        next.remove();
        fireChange();
        return true;
      }
      return false;
    },
    [fireChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === '@') {
        // Let the "@" be typed for visual feedback, then open the picker.
        // We'll strip the "@" character when the user picks.
        requestAnimationFrame(() => openPickerAtCursor());
        return;
      }
      if (e.key === 'Enter' && flavour === 'heading') {
        e.preventDefault();
        return;
      }
      if (e.key === 'Escape' && pickerOpen) {
        e.preventDefault();
        setPickerOpen(false);
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (tryDeleteAdjacentChip(e.key)) {
          e.preventDefault();
        }
      }
    },
    [flavour, openPickerAtCursor, pickerOpen, tryDeleteAdjacentChip]
  );

  const handleClick = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const chip = target.closest<HTMLElement>(`[${VAR_PATH_ATTR}]`);
    if (!chip) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const filtersAttr = chip.getAttribute(VAR_FILTERS_ATTR);
    let filters: FilterApplication[] = [];
    if (filtersAttr) {
      try {
        filters = JSON.parse(filtersAttr);
      } catch {
        filters = [];
      }
    }
    setChipEditor({
      element: chip,
      rect: chip.getBoundingClientRect(),
      path: chip.getAttribute(VAR_PATH_ATTR) ?? '',
      filters,
    });
  }, []);

  const updateChipFilters = useCallback(
    (newFilters: FilterApplication[]) => {
      if (!chipEditor) {
        return;
      }
      const { element, path } = chipEditor;
      if (newFilters.length > 0) {
        element.setAttribute(VAR_FILTERS_ATTR, JSON.stringify(newFilters));
      } else {
        element.removeAttribute(VAR_FILTERS_ATTR);
      }
      element.textContent = chipLabelWithFilters(labelForPath(path), newFilters);
      setChipEditor({ ...chipEditor, filters: newFilters });
      fireChange();
    },
    [chipEditor, fireChange]
  );

  const removeChip = useCallback(() => {
    if (!chipEditor) {
      return;
    }
    chipEditor.element.remove();
    setChipEditor(null);
    fireChange();
  }, [chipEditor, fireChange]);

  const insertVariable = useCallback(
    (path: string, label: string, filters: FilterApplication[] = []) => {
      const range = triggerRangeRef.current;
      if (!range || !ref.current) {
        setPickerOpen(false);
        return;
      }
      try {
        const start = range.startContainer;
        if (start.nodeType === Node.TEXT_NODE) {
          const text = start.textContent ?? '';
          const cursorOffset = range.startOffset;
          const beforeCursor = text.slice(0, cursorOffset);
          const atIdx = beforeCursor.lastIndexOf('@');
          if (atIdx !== -1) {
            const newRange = document.createRange();
            newRange.setStart(start, atIdx);
            newRange.setEnd(start, cursorOffset);
            newRange.deleteContents();
            const chip = makeChipElement(path, label, filters, {
              chipClassName: classes.varChip,
            });
            newRange.insertNode(chip);
            // Pad with caret-safe text nodes so the user can type immediately
            // before / after the chip even if it's the first or last child.
            ensureChipCaretSafety(ref.current);
            const trailing = chip.nextSibling;
            const after = document.createRange();
            // Position cursor inside the trailing ZWSP node (offset 1, after
            // the ZWSP char) so the next keystroke types at a real caret
            // location rather than getting trapped on the chip boundary.
            if (trailing && trailing.nodeType === Node.TEXT_NODE) {
              after.setStart(trailing, Math.min(1, trailing.textContent?.length ?? 0));
            } else {
              after.setStartAfter(chip);
            }
            after.collapse(true);
            const sel = globalThis.getSelection?.();
            sel?.removeAllRanges();
            sel?.addRange(after);
          }
        }
      } catch {
        // If anything goes wrong, just close the picker — user can try again.
      }
      setPickerOpen(false);
      fireChange();
    },
    [fireChange]
  );

  const filteredVariables = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) {
      return variables;
    }
    return variables.filter(
      (v) => v.label.toLowerCase().includes(q) || v.path.toLowerCase().includes(q)
    );
  }, [pickerQuery, variables]);

  const grouped = useMemo(() => {
    const out = new Map<string, VariableOption[]>();
    for (const v of filteredVariables) {
      if (!out.has(v.group)) {
        out.set(v.group, []);
      }
      out.get(v.group)!.push(v);
    }
    return out;
  }, [filteredVariables]);

  return (
    <>
      <Popover
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        position="bottom-start"
        withinPortal
        shadow="md"
      >
        <Popover.Target>
          {/* role="textbox" on a contentEditable element is the documented
              ARIA pattern for rich-text editors that need inline non-editable
              tokens (the variable chips). <input>/<textarea> don't support
              inline children — they only hold a string. */}
          <div
            ref={ref}
            className={`${classes.inlineEditor} ${flavour === 'heading' ? classes.inlineHeading : ''}`}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            onBlur={fireChange}
            role="textbox"
            tabIndex={0}
            aria-multiline={flavour === 'paragraph'}
            aria-label={ariaLabel}
            data-placeholder={placeholder}
          />
        </Popover.Target>
        <Popover.Dropdown p="xs">
          <Stack gap="xs" style={{ width: 280 }}>
            <TextInput
              size="xs"
              placeholder="Search variables…"
              value={pickerQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPickerQuery(e.currentTarget.value)}
              autoFocus
            />
            <ScrollArea h={240}>
              <Combobox store={combobox} onOptionSubmit={() => {}}>
                <Combobox.Options>
                  {grouped.size === 0 ? (
                    <Text c="dimmed" size="xs" px="xs" py="md">
                      No matches.
                    </Text>
                  ) : (
                    [...grouped.entries()].map(([group, opts]) => (
                      <Combobox.Group key={group} label={group}>
                        {opts.map((opt) => (
                          <Combobox.Option
                            key={opt.path}
                            value={opt.path}
                            onClick={() => insertVariable(opt.path, opt.label)}
                          >
                            <Stack gap={0}>
                              <Text size="sm">{opt.label}</Text>
                              {opt.description && (
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {opt.description}
                                </Text>
                              )}
                            </Stack>
                          </Combobox.Option>
                        ))}
                      </Combobox.Group>
                    ))
                  )}
                </Combobox.Options>
              </Combobox>
            </ScrollArea>
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {chipEditor && (
        <ChipFilterPopover
          state={chipEditor}
          filterOptions={filterOptions}
          onFiltersChange={updateChipFilters}
          onRemoveChip={removeChip}
          onClose={() => setChipEditor(null)}
        />
      )}
    </>
  );
}

// ---------- chip-filter popover ----------

interface ChipFilterPopoverProps {
  state: ChipEditorState;
  filterOptions: FilterOption[];
  onFiltersChange: (next: FilterApplication[]) => void;
  onRemoveChip: () => void;
  onClose: () => void;
}

const POPOVER_WIDTH = 320;
const VIEWPORT_MARGIN = 8;
const CHIP_GAP = 4;

type PopoverPosition =
  | { mode: 'below'; top: number; left: number; maxHeight: number }
  | { mode: 'above'; bottom: number; left: number; maxHeight: number };

function ChipFilterPopover({
  state,
  filterOptions,
  onFiltersChange,
  onRemoveChip,
  onClose,
}: Readonly<ChipFilterPopoverProps>) {
  const [adding, setAdding] = useState<string | null>(null);
  const [argDraft, setArgDraft] = useState('');
  const combobox = useCombobox();
  // Callback ref backed by state. Mantine's <Portal> renders null on its first
  // render and only mounts its target node inside its own useLayoutEffect — so
  // a plain RefObject is null when our effect first runs and never re-fires.
  // Using setState as the ref forces our layout effect to re-run once the
  // element actually mounts.
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<PopoverPosition>(() => ({
    mode: 'below',
    top: state.rect.bottom + CHIP_GAP,
    left: state.rect.left,
    maxHeight: Math.max(0, globalThis.innerHeight - state.rect.bottom - VIEWPORT_MARGIN - CHIP_GAP),
  }));

  // Click-outside: close the popover when the user clicks anywhere outside the
  // Paper. (We can't use Mantine's useClickOutside because it returns a
  // RefObject, which would hit the same Portal-timing problem as positioning.)
  useEffect(() => {
    if (!el) {
      return;
    }
    const handler = (event: MouseEvent | TouchEvent) => {
      if (!el.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [el, onClose]);

  // Choose a side (above/below the chip) once on mount or when the chip
  // changes, and size maxHeight to the available space on that side. When
  // flipped above, anchor the popover by its bottom edge so it grows upward
  // naturally as the user adds filters — instead of staying pinned at top and
  // creeping back down into the chip. Content that exceeds maxHeight scrolls
  // inside the popover.
  useLayoutEffect(() => {
    if (!el) {
      return;
    }
    const vh = globalThis.innerHeight;
    const vw = globalThis.innerWidth;
    const naturalHeight = el.scrollHeight;

    const spaceBelow = vh - state.rect.bottom - VIEWPORT_MARGIN - CHIP_GAP;
    const spaceAbove = state.rect.top - VIEWPORT_MARGIN - CHIP_GAP;
    // Prefer below; only flip above if content doesn't fit below and above
    // actually has more room.
    const goAbove = naturalHeight > spaceBelow && spaceAbove > spaceBelow;

    let left = state.rect.left;
    if (left + POPOVER_WIDTH > vw - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, vw - POPOVER_WIDTH - VIEWPORT_MARGIN);
    }

    const next: PopoverPosition = goAbove
      ? {
          mode: 'above',
          bottom: vh - state.rect.top + CHIP_GAP,
          left,
          maxHeight: Math.max(0, spaceAbove),
        }
      : {
          mode: 'below',
          top: state.rect.bottom + CHIP_GAP,
          left,
          maxHeight: Math.max(0, spaceBelow),
        };

    setPosition(next);
  }, [el, state.rect]);

  const removeFilter = (idx: number) => {
    const next = state.filters.filter((_, i) => i !== idx);
    onFiltersChange(next);
  };

  const beginAdd = (name: string) => {
    setAdding(name);
    setArgDraft('');
    combobox.closeDropdown();
  };

  const confirmAdd = () => {
    if (!adding) {
      return;
    }
    onFiltersChange([...state.filters, { name: adding, args: argDraft.trim() ? [argDraft] : [] }]);
    setAdding(null);
    setArgDraft('');
  };

  return (
    <Portal>
      <Paper
        ref={setEl}
        shadow="md"
        p="sm"
        withBorder
        style={{
          position: 'fixed',
          ...(position.mode === 'below' ? { top: position.top } : { bottom: position.bottom }),
          left: position.left,
          zIndex: 1000,
          width: POPOVER_WIDTH,
          maxHeight: position.maxHeight,
          overflowY: 'auto',
        }}
      >
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Variable
            </Text>
            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={onClose}
              aria-label="Close filter editor"
            >
              <IconX size={12} />
            </ActionIcon>
          </Group>
          <Code style={{ fontSize: 12 }}>{`{{ ${state.path}${state.filters
            .map((f) => filterToJinja(f))
            .join('')} }}`}</Code>

          {state.filters.length > 0 && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Applied filters
              </Text>
              {state.filters.map((f, idx) => (
                <Group
                  key={`${f.name}-${idx}`}
                  gap={2}
                  wrap="nowrap"
                  style={{
                    background: 'var(--mantine-color-default-hover)',
                    borderRadius: 4,
                    padding: '2px 4px 2px 8px',
                  }}
                >
                  <Code style={{ background: 'transparent', fontSize: 12 }}>
                    {filterToJinja(f).trim()}
                  </Code>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={() => removeFilter(idx)}
                    aria-label="Remove filter"
                  >
                    <IconX size={12} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}

          {adding ? (
            <Group gap="xs" wrap="nowrap">
              <Text size="xs">| {adding}(</Text>
              <TextInput
                size="xs"
                placeholder="arg (optional)"
                value={argDraft}
                onChange={(e) => setArgDraft(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmAdd();
                  }
                }}
                style={{ flex: 1 }}
                autoFocus
              />
              <Text size="xs">)</Text>
              <Button size="xs" onClick={confirmAdd}>
                Add
              </Button>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => {
                  setAdding(null);
                  setArgDraft('');
                }}
              >
                Cancel
              </Button>
            </Group>
          ) : (
            <Combobox
              store={combobox}
              onOptionSubmit={(value) => beginAdd(value)}
              // Render in a portal so the dropdown isn't clipped by the
              // chip popover's overflow: auto when it flips above the button
              // (happens when the popover sits high in the viewport).
              withinPortal
              // Stack above the chip popover (which sits at zIndex 1000)
              // — Mantine's default popover z-index would otherwise place
              // this dropdown underneath the chip popover.
              zIndex={1100}
            >
              <Combobox.Target>
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconPlus size={12} />}
                  onClick={() => combobox.toggleDropdown()}
                  disabled={filterOptions.length === 0}
                >
                  Add filter
                </Button>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Options>
                  {filterOptions.map((f) => (
                    <Combobox.Option key={f.name} value={f.name}>
                      <Stack gap={0}>
                        <Text size="sm">{f.name}</Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {f.description}
                        </Text>
                      </Stack>
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          )}

          <Button
            size="xs"
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={12} />}
            onClick={onRemoveChip}
          >
            Remove variable
          </Button>
        </Stack>
      </Paper>
    </Portal>
  );
}

// DOM helpers extracted to `./inline-editor-dom.ts` so they're testable in
// jsdom without rendering this React component (whose interactivity depends
// on contentEditable + window.getSelection, neither of which jsdom emulates
// faithfully).
